import { environment } from "@/config";
import posthog, { posthogEnabled } from "@/posthog";
import { markErrorAsExpected } from "@/services/expected-error";

import * as Sentry from "@sentry/react";

const recordedErrors = new WeakMap();

function sanitizeString(value) {
  return String(value)
    .replace(
      /([?&](?:access_token|api[_-]?key|authorization|client[_-]?secret|code|credential|otp|password|redirect|refresh_token|secret|session_id|token)=)[^&\s]+/gi,
      "$1[REDACTED]"
    )
    .replace(
      /\bAuthorization\s*[:=]\s*(?:(?:Basic|Bearer)\s+)?[A-Za-z0-9._~+/=-]+/gi,
      "Authorization=[REDACTED]"
    )
    .replace(
      /\b(api[_-]?key|client[_-]?secret|credential|otp|password|refresh[_-]?token|secret|session[_-]?id|token)\s*[:=]\s*(?:"[^"]*"|'[^']*'|[^\s,;&]+)/gi,
      "$1=[REDACTED]"
    )
    .replace(/Bearer\s+[A-Za-z0-9._~+/=-]+/gi, "Bearer [REDACTED]")
    .replace(/\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}\b/g, "[EMAIL]")
    .replace(/(?:\+\d{1,3}[ .-]?)?(?:\d[ .-]?){9,14}\d/g, "[PHONE]");
}

function isSensitiveKey(key) {
  return /authorization|body|cookie|credential|email|message|note|otp|password|phone|secret|token/i.test(
    key
  );
}

function sanitizeAttributeValue(value) {
  if (typeof value === "string") return sanitizeString(value);
  if (["boolean", "number"].includes(typeof value)) return value;
  if (value === null) return null;
  if (!Array.isArray(value)) return undefined;

  const values = value
    .filter(
      (item) =>
        item === null || ["boolean", "number", "string"].includes(typeof item)
    )
    .map((item) => (typeof item === "string" ? sanitizeString(item) : item));

  return values;
}

function sanitizeAttributes(attributes) {
  if (!attributes || typeof attributes !== "object") return {};
  if (Array.isArray(attributes)) return {};

  return Object.fromEntries(
    Object.entries(attributes)
      .filter(([key]) => !isSensitiveKey(key))
      .map(([key, value]) => [key, sanitizeAttributeValue(value)])
      .filter(([, value]) => value !== undefined)
  );
}

function createErrorId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID();
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

function normalizeError(error) {
  if (error && ["function", "object"].includes(typeof error)) {
    const recordedError = recordedErrors.get(error);
    if (recordedError) return recordedError;
  }

  const normalizedError = new Error(
    sanitizeString(error?.message || error?.code || error || "Unknown error")
  );
  normalizedError.name = sanitizeString(error?.name || "Error");
  if (error?.stack) normalizedError.stack = sanitizeString(error.stack);

  const recordedError = {
    error: normalizedError,
    id: createErrorId(),
    reported: false,
  };

  if (error && ["function", "object"].includes(typeof error)) {
    recordedErrors.set(error, recordedError);
  }

  return recordedError;
}

export function recordError(
  error,
  { source, operation, expected, severity, attributes = {}, groupingKey } = {}
) {
  if (!error) return;
  if (environment !== "production") return;

  const recordedError = normalizeError(error);
  const sanitizedAttributes = sanitizeAttributes(attributes);
  if (expected === true) {
    markErrorAsExpected(error);
    recordedError.expected = true;
  }
  if (expected === false && recordedError.expected !== true)
    recordedError.expected = false;
  if (severity) recordedError.severity = severity;
  const errorExpected = recordedError.expected || false;
  const errorSeverity =
    recordedError.severity || (errorExpected ? "info" : "error");
  const sanitizedGroupingKey = Array.isArray(groupingKey)
    ? groupingKey.map(sanitizeString)
    : undefined;
  if (errorExpected) return;
  if (recordedError.reported) return;
  recordedError.reported = true;

  try {
    Sentry.captureException(recordedError.error, {
      tags: {
        error_id: recordedError.id,
        expected: false,
        operation: operation ? sanitizeString(operation) : undefined,
        source: source ? sanitizeString(source) : undefined,
      },
      extra: sanitizedAttributes,
      fingerprint: sanitizedGroupingKey,
      level: errorSeverity,
    });
  } catch {
    // Error reporting must never interrupt the user flow.
  }

  if (!posthogEnabled) return;

  try {
    posthog.captureException(recordedError.error, {
      ...sanitizedAttributes,
      $exception_fingerprint: sanitizedGroupingKey?.join("|"),
      $exception_level: errorSeverity,
      error_id: recordedError.id,
      expected: false,
      operation: operation ? sanitizeString(operation) : undefined,
      source: source ? sanitizeString(source) : undefined,
    });
  } catch {
    // Error reporting must never interrupt the user flow.
  }
}
